import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import SubTitle2, { Subtitle2Properties } from '../Typography/SubTitle2';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';

const ListItem = styled.li`
  min-height: 20px;
  display: inline-block;
  vertical-align: middle;
  color: ${overlay[600]};
`;

const StyledSubtitle = styled(
  (properties: Subtitle2Properties) => <SubTitle2 {...properties} />,
)`
  && {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    line-height: 14px;
    font-weight: normal;
  }
  color: ${overlay[600]};
  display: block;
  padding: 4px 0;
  a, span {
    color: ${overlay[600]};
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  a:hover {
    color: ${overlay[900]};
  }
`;

const TruncatedListItem = styled.li`
  min-height: 20px;
  vertical-align: middle;
  overflow: hidden;
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
  display: inline-block;
`;

const Separator = styled.li`
  display: inline-block;
  vertical-align: middle;
  margin: 0 8px;
  font-family: ${({ theme }) => theme.fonts.secondary};
  color: ${grey[325]};
  position: relative;
  top: 1px;

  :before {
   content: '/';
  }
`;

const StyledUl = styled.ul`
  padding: 0 16px 0 0;
  margin: 2px 0 0 0;
  min-height: 20px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: baseline;
`;

const Breadcrumb = ({ children }: ComponentProperties) => (
  <ListItem>
    <StyledSubtitle tag="span">
      {children}
    </StyledSubtitle>
  </ListItem>
);

const TruncatedBreadcrumb = ({ children }: ComponentProperties) => (
  <TruncatedListItem>
    <StyledSubtitle tag="span">
      {children}
    </StyledSubtitle>
  </TruncatedListItem>
);

const breadcrumbsPropertyTypes = {
  crumbs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
  ).isRequired,
  className: PropTypes.string,
};

type BreadcrumbsProperties = InferProps<typeof breadcrumbsPropertyTypes> & {
  className?: string;
};

const Breadcrumbs = ({
  crumbs, className,
}: BreadcrumbsProperties) => (crumbs.length > 0 ? (
  <StyledUl className={className}>
    {crumbs.map((crumb, index, array) => {
      const keyIndex = index * 2;
      return array.length - 1 === index
        ? (
          <TruncatedBreadcrumb key={keyIndex}>
            {crumb}
          </TruncatedBreadcrumb>
        )
        : (
          <React.Fragment key={keyIndex}>
            <Breadcrumb>
              {crumb}
            </Breadcrumb>
            <Separator key={keyIndex + 1} />
          </React.Fragment>
        );
    })}
  </StyledUl>
// eslint-disable-next-line unicorn/no-null
) : null);

Breadcrumb.propTypes = {
  children: PropTypes.node,
};

TruncatedBreadcrumb.propTypes = {
  children: PropTypes.node,
};

Breadcrumbs.propTypes = breadcrumbsPropertyTypes;

export { Breadcrumbs, Separator };
